/* ---------------------------------
Search Input Validation & Sanitization
--------------------------------- */

import DOMPurify from 'isomorphic-dompurify'

export interface ValidationResult {
  isValid: boolean
  sanitizedValue: string
  error?: string
}

export interface SearchValidationOptions {
  minLength?: number
  maxLength?: number
  allowedSymbols?: string[]
  allowAccents?: boolean
}

const DEFAULT_OPTIONS: Required<SearchValidationOptions> = {
  minLength: 2,
  maxLength: 50,
  allowedSymbols: [' ', "'", '-', '.'],
  allowAccents: true,
}

/**
 * Validates and sanitizes search input for Italian location names
 * @param input - Raw user input
 * @param options - Validation options
 * @returns ValidationResult with sanitized value and validation status
 */
export function validateSearchInput(
  input: string,
  options: SearchValidationOptions = {}
): ValidationResult {
  const opts = { ...DEFAULT_OPTIONS, ...options }

  // Early return for empty input
  if (!input || typeof input !== 'string') {
    return {
      isValid: false,
      sanitizedValue: '',
      error: 'Inserisci un termine di ricerca',
    }
  }

  // Step 1: Basic sanitization - remove HTML/scripts and normalize spaces
  let sanitized = DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  })

  // Normalize whitespace: trim and collapse multiple spaces
  sanitized = sanitized.replace(/\s+/g, ' ').trim()

  // Step 2: Length validation
  if (sanitized.length < opts.minLength) {
    return {
      isValid: false,
      sanitizedValue: sanitized,
      error: `Inserisci almeno ${opts.minLength} caratteri`,
    }
  }

  if (sanitized.length > opts.maxLength) {
    return {
      isValid: false,
      sanitizedValue: sanitized,
      error: `Massimo ${opts.maxLength} caratteri consentiti`,
    }
  }

  // Step 3: Character validation for Italian location names
  const basePattern = opts.allowAccents
    ? /^[a-zA-ZÀ-ÿ\u0100-\u017F\u1E00-\u1EFF]/ // Letters with accents/diacritics
    : /^[a-zA-Z]/

  // Build allowed characters pattern
  const allowedSymbolsEscaped = opts.allowedSymbols
    .map((symbol) => symbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('')

  const fullPattern = new RegExp(
    `^[a-zA-Z${opts.allowAccents ? 'À-ÿ\\u0100-\\u017F\\u1E00-\\u1EFF' : ''}${allowedSymbolsEscaped}]+$`
  )

  // Check if input contains only allowed characters
  if (!fullPattern.test(sanitized)) {
    return {
      isValid: false,
      sanitizedValue: sanitized,
      error:
        'Usa solo lettere, spazi e caratteri consentiti (apostrofi, trattini)',
    }
  }

  // Step 4: Additional validation rules

  // Must start with a letter
  if (!basePattern.test(sanitized)) {
    return {
      isValid: false,
      sanitizedValue: sanitized,
      error: 'Il nome deve iniziare con una lettera',
    }
  }

  // No consecutive special characters
  if (/['\-\.]{2,}/.test(sanitized)) {
    return {
      isValid: false,
      sanitizedValue: sanitized,
      error: 'Evita caratteri speciali consecutivi',
    }
  }

  // No leading/trailing special characters (except spaces, already trimmed)
  if (/^['\-\.]|['\-\.]$/.test(sanitized)) {
    return {
      isValid: false,
      sanitizedValue: sanitized,
      error: 'Il nome non può iniziare o finire con caratteri speciali',
    }
  }

  return {
    isValid: true,
    sanitizedValue: sanitized,
  }
}

/**
 * Quick validation for real-time feedback (less strict)
 * @param input - Current input value
 * @returns boolean indicating if input is potentially valid
 */
export function isValidSearchInputRealtime(input: string): boolean {
  if (!input || input.length === 0) return true // Allow empty during typing

  const { minLength, maxLength, allowedSymbols } = DEFAULT_OPTIONS

  // Allow partial input during typing
  if (input.length < minLength && input.length > 0) return true
  if (input.length > maxLength) return false

  // Basic character check
  const allowedSymbolsEscaped = allowedSymbols
    .map((symbol) => symbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('')

  const pattern = new RegExp(
    `^[a-zA-ZÀ-ÿ\\u0100-\\u017F\\u1E00-\\u1EFF${allowedSymbolsEscaped}\\s]*$`
  )

  return pattern.test(input)
}

/**
 * Get user-friendly validation messages for common Italian locations
 */
export const VALIDATION_MESSAGES = {
  EMPTY: 'Inserisci il nome di una città o regione',
  TOO_SHORT: 'Inserisci almeno 2 caratteri',
  TOO_LONG: 'Massimo 50 caratteri consentiti',
  INVALID_CHARS: 'Usa solo lettere, spazi e caratteri consentiti',
  INVALID_START: 'Il nome deve iniziare con una lettera',
  CONSECUTIVE_SPECIAL: 'Evita caratteri speciali consecutivi',
  INVALID_BOUNDARIES:
    'Il nome non può iniziare o finire con caratteri speciali',
  SANITIZATION_FAILED: 'Contenuto non valido rilevato',
} as const
